// ═══════════════════════════════════════════════════════════════
// RollCall — Reports
// ICS 211, meal counts, personnel hours, staffing, roster export
// CSV and PDF export
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import {
  Title, Text, Paper, Group, Button, Select, TextInput, Badge,
} from '@mantine/core';
import {
  IconChartBar, IconFileTypeCsv, IconFileTypePdf,
} from '@tabler/icons-react';
import Papa from 'papaparse';
import { jsPDF } from 'jspdf';
import { notifications } from '@mantine/notifications';
import { useAuthStore } from '../../store/authStore';
import { useRollCallStore } from '../../store/rollcallStore';

const REPORT_TYPES = [
  { value: 'ics211', label: 'ICS 211 — Incident Check-In List' },
  { value: 'meals', label: 'Meal Count Report' },
  { value: 'hours', label: 'Personnel Hours Report' },
  { value: 'staffing', label: 'Daily Staffing Summary' },
  { value: 'roster', label: 'Personnel Roster Export' },
];


export default function Reports() {
  const { incidents, loadIncidents } = useAuthStore();
  const { events, checkins, personnel, loadAll } = useRollCallStore();

  const [reportType, setReportType] = useState<string | null>(null);
  const [incidentFilter, setIncidentFilter] = useState<string | null>(null);
  const [eventFilter, setEventFilter] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [reportData, setReportData] = useState<any>(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => { loadIncidents(); loadAll(); }, []);

  const filteredEvents = events.filter(e => {
    if (incidentFilter && String(e.incidentId) !== incidentFilter) return false;
    if (dateFrom && e.eventDate < dateFrom) return false;
    if (dateTo && e.eventDate > dateTo) return false;
    return true;
  });

  const getCheckinsForEvents = (evtList: typeof events) => {
    const evtIds = new Set(evtList.map(e => String(e.id)));
    return checkins
      .filter(c => evtIds.has(String(c.eventId)))
      .map(c => {
        const evt = evtList.find(e => String(e.id) === String(c.eventId));
        return { ...c, eventName: evt?.name || '', eventType: evt?.eventType || '', mealType: evt?.mealType || '', eventDate: evt?.eventDate || '' };
      });
  };

  const handleGenerate = () => {
    setGenerating(true);
    try {
      if (reportType === 'roster') {
        setReportData({ type: 'roster', rows: personnel });
      } else if (reportType === 'ics211') {
        if (!eventFilter) { notifications.show({ message: 'Select an event for ICS 211', color: 'yellow' }); setGenerating(false); return; }
        const evtCheckins = checkins.filter(c => String(c.eventId) === eventFilter);
        const event = events.find(e => String(e.id) === eventFilter);
        setReportData({ type: 'ics211', checkins: evtCheckins, event });
      } else if (reportType === 'meals') {
        const mealEvts = filteredEvents.filter(e => e.eventType === 'meal');
        const allCheckins = getCheckinsForEvents(mealEvts);
        setReportData({ type: 'meals', checkins: allCheckins, events: mealEvts });
      } else if (reportType === 'hours') {
        const shiftEvts = filteredEvents.filter(e => e.eventType === 'shift');
        const allCheckins = getCheckinsForEvents(shiftEvts);
        const hoursMap: Record<string, { personnelName: string; agency: string; position: string; totalHours: number; shifts: number }> = {};
        allCheckins.forEach(c => {
          if (!c.checkInTime) return;
          const key = c.personnelId || c.personnelName;
          if (!hoursMap[key]) hoursMap[key] = { personnelName: c.personnelName, agency: c.agency, position: c.position, totalHours: 0, shifts: 0 };
          hoursMap[key].shifts++;
          if (c.checkOutTime) {
            hoursMap[key].totalHours += (new Date(c.checkOutTime).getTime() - new Date(c.checkInTime).getTime()) / 3600000;
          }
        });
        setReportData({ type: 'hours', rows: Object.values(hoursMap).sort((a, b) => b.totalHours - a.totalHours) });
      } else if (reportType === 'staffing') {
        const allCheckins = getCheckinsForEvents(filteredEvents);
        const dateMap: Record<string, { date: string; total: number; shifts: number; meals: number; trainings: number; briefings: number; uniquePersonnel: Set<string> }> = {};
        allCheckins.forEach(c => {
          const date = c.eventDate || (c.checkInTime ? new Date(c.checkInTime).toISOString().slice(0, 10) : 'Unknown');
          if (!dateMap[date]) dateMap[date] = { date, total: 0, shifts: 0, meals: 0, trainings: 0, briefings: 0, uniquePersonnel: new Set() };
          dateMap[date].total++;
          dateMap[date].uniquePersonnel.add(c.personnelId || c.personnelName);
          if (c.eventType === 'shift') dateMap[date].shifts++;
          else if (c.eventType === 'meal') dateMap[date].meals++;
          else if (c.eventType === 'training') dateMap[date].trainings++;
          else if (c.eventType === 'briefing') dateMap[date].briefings++;
        });
        setReportData({
          type: 'staffing',
          rows: Object.values(dateMap)
            .map(d => ({ ...d, uniqueCount: d.uniquePersonnel.size }))
            .sort((a, b) => a.date.localeCompare(b.date)),
        });
      }
    } catch (err: any) {
      notifications.show({ message: 'Failed to generate: ' + (err?.message || ''), color: 'red' });
    } finally {
      setGenerating(false);
    }
  };

  const fmtTime = (ts: string) => ts ? new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
  const fmtDate = () => new Date().toISOString().slice(0, 10);

  const exportCSV = () => {
    if (!reportData) return;
    let data: Record<string, string | number>[] = [];
    if (reportData.type === 'roster') {
      data = reportData.rows.map((p: any) => ({ Name: `${p.firstName} ${p.lastName}`, Agency: p.agency, Position: p.position, PIN: p.pin, 'Badge ID': p.badgeId, Phone: p.phone, Email: p.email, Status: p.status }));
    } else if (reportData.type === 'ics211') {
      data = reportData.checkins.map((c: any) => ({ Name: c.personnelName, Agency: c.agency, Position: c.position, 'Check In': fmtTime(c.checkInTime), 'Check Out': fmtTime(c.checkOutTime), Method: c.method }));
    } else if (reportData.type === 'meals') {
      data = reportData.checkins.map((c: any) => ({ Name: c.personnelName, Agency: c.agency, Event: c.eventName, 'Meal Type': c.mealType, Date: c.eventDate, Time: fmtTime(c.checkInTime) }));
    } else if (reportData.type === 'hours') {
      data = reportData.rows.map((r: any) => ({ Name: r.personnelName, Agency: r.agency, Position: r.position, Shifts: r.shifts, 'Total Hours': r.totalHours.toFixed(1) }));
    } else if (reportData.type === 'staffing') {
      data = reportData.rows.map((r: any) => ({ Date: r.date, 'Unique Personnel': r.uniqueCount, 'Total Check-ins': r.total, Shifts: r.shifts, Meals: r.meals, Trainings: r.trainings, Briefings: r.briefings }));
    }
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `rollcall_${reportType}_${fmtDate()}.csv`; a.click();
    URL.revokeObjectURL(url);
    notifications.show({ message: 'CSV exported', color: 'green' });
  };

  const exportPDF = () => {
    if (!reportData) return;
    const pdf = new jsPDF();
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.text('RollCall Report', 14, 20);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);
    pdf.text(`Report: ${REPORT_TYPES.find(r => r.value === reportType)?.label || ''}`, 14, 34);
    if (incidentFilter) {
      const inc = incidents.find(i => String(i.id) === incidentFilter);
      if (inc) pdf.text(`Incident: ${inc.name}`, 14, 40);
    }

    let y = incidentFilter ? 50 : 44;
    const addRow = (cols: { x: number; maxLen?: number }[], values: string[], bold = false) => {
      if (y > 280) { pdf.addPage(); y = 20; }
      pdf.setFont('helvetica', bold ? 'bold' : 'normal');
      pdf.setFontSize(9);
      cols.forEach((col, i) => {
        pdf.text(String(values[i] || '').slice(0, col.maxLen || 30), col.x, y);
      });
      y += 5;
    };

    if (reportData.type === 'ics211') {
      const cols = [{ x: 14, maxLen: 25 }, { x: 64, maxLen: 22 }, { x: 110, maxLen: 18 }, { x: 148, maxLen: 12 }, { x: 178, maxLen: 12 }];
      addRow(cols, ['Name', 'Agency', 'Position', 'Check In', 'Check Out'], true);
      y += 1;
      reportData.checkins.forEach((c: any) => addRow(cols, [c.personnelName, c.agency, c.position, fmtTime(c.checkInTime), fmtTime(c.checkOutTime)]));
    } else if (reportData.type === 'roster') {
      const cols = [{ x: 14, maxLen: 28 }, { x: 64, maxLen: 28 }, { x: 120, maxLen: 18 }, { x: 160, maxLen: 10 }, { x: 180, maxLen: 10 }];
      addRow(cols, ['Name', 'Agency', 'Position', 'PIN', 'Status'], true);
      y += 1;
      reportData.rows.forEach((p: any) => addRow(cols, [`${p.firstName} ${p.lastName}`, p.agency, p.position, p.pin, p.status]));
    } else if (reportData.type === 'meals') {
      const cols = [{ x: 14, maxLen: 25 }, { x: 60, maxLen: 22 }, { x: 104, maxLen: 22 }, { x: 148, maxLen: 10 }, { x: 166, maxLen: 12 }, { x: 186, maxLen: 8 }];
      addRow(cols, ['Name', 'Agency', 'Event', 'Meal', 'Date', 'Time'], true);
      y += 1;
      reportData.checkins.forEach((c: any) => addRow(cols, [c.personnelName, c.agency, c.eventName, c.mealType, c.eventDate, fmtTime(c.checkInTime)]));
    } else if (reportData.type === 'hours') {
      const cols = [{ x: 14, maxLen: 28 }, { x: 70, maxLen: 28 }, { x: 126, maxLen: 18 }, { x: 162, maxLen: 8 }, { x: 180, maxLen: 10 }];
      addRow(cols, ['Name', 'Agency', 'Position', 'Shifts', 'Hours'], true);
      y += 1;
      reportData.rows.forEach((r: any) => addRow(cols, [r.personnelName, r.agency, r.position, String(r.shifts), r.totalHours.toFixed(1)]));
    } else if (reportData.type === 'staffing') {
      const cols = [{ x: 14, maxLen: 12 }, { x: 40, maxLen: 10 }, { x: 66, maxLen: 10 }, { x: 92, maxLen: 8 }, { x: 114, maxLen: 8 }, { x: 136, maxLen: 10 }, { x: 162, maxLen: 10 }];
      addRow(cols, ['Date', 'Personnel', 'Check-ins', 'Shifts', 'Meals', 'Trainings', 'Briefings'], true);
      y += 1;
      reportData.rows.forEach((r: any) => addRow(cols, [r.date, String(r.uniqueCount), String(r.total), String(r.shifts), String(r.meals), String(r.trainings), String(r.briefings)]));
    }

    pdf.save(`rollcall_${reportType}_${fmtDate()}.pdf`);
    notifications.show({ message: 'PDF exported', color: 'green' });
  };

  const rowCount = reportData
    ? (reportData.rows?.length || reportData.checkins?.length || 0)
    : 0;

  return (
    <div>
      <Title order={3} mb="md" c="black">Reports</Title>

      <Paper p="lg" radius="sm" withBorder mb="md" maw={700}>
        <Select label="Report Type" placeholder="Choose a report" data={REPORT_TYPES}
          value={reportType} onChange={v => { setReportType(v); setReportData(null); }} mb="md" />

        {reportType && reportType !== 'roster' && (
          <>
            <Group grow mb="md">
              <Select label="Incident" placeholder="All incidents" clearable
                data={incidents.map(i => ({ value: String(i.id), label: i.name }))}
                value={incidentFilter} onChange={setIncidentFilter} />
              {reportType === 'ics211' && (
                <Select label="Event" placeholder="Select event" required
                  data={filteredEvents.map(e => ({ value: String(e.id), label: `${e.name} (${e.eventDate})` }))}
                  value={eventFilter} onChange={setEventFilter} />
              )}
            </Group>
            <Group grow mb="md">
              <TextInput label="From" type="date" value={dateFrom} onChange={e => setDateFrom(e.currentTarget.value)} />
              <TextInput label="To" type="date" value={dateTo} onChange={e => setDateTo(e.currentTarget.value)} />
            </Group>
          </>
        )}

        <Group>
          <Button size="xs" onClick={handleGenerate} loading={generating}
            leftSection={<IconChartBar size={14} />}>Generate</Button>
          {reportData && <Button size="xs" variant="light" onClick={exportCSV}
            leftSection={<IconFileTypeCsv size={14} />}>Export CSV</Button>}
          {reportData && <Button size="xs" variant="light" onClick={exportPDF}
            leftSection={<IconFileTypePdf size={14} />}>Export PDF</Button>}
        </Group>
      </Paper>

      {/* Preview */}
      {reportData && (
        <Paper p="md" radius="sm" withBorder>
          <Group mb="sm">
            <Text fw={600} size="sm">Preview</Text>
            <Badge variant="light">{rowCount} rows</Badge>
          </Group>

          <div style={{ overflowX: 'auto' }}>
            {reportData.type === 'ics211' && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ borderBottom: '2px solid var(--frost)' }}>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Name</th><th style={{ textAlign: 'left', padding: '8px' }}>Agency</th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Position</th><th style={{ textAlign: 'left', padding: '8px' }}>In</th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Out</th><th style={{ textAlign: 'left', padding: '8px' }}>Method</th>
                </tr></thead>
                <tbody>
                  {reportData.checkins.map((c: any, i: number) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--frost)' }}>
                      <td style={{ padding: '6px 8px', fontWeight: 600 }}>{c.personnelName}</td>
                      <td style={{ padding: '6px 8px' }}>{c.agency}</td>
                      <td style={{ padding: '6px 8px' }}>{c.position}</td>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 12 }}>{fmtTime(c.checkInTime)}</td>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 12 }}>{fmtTime(c.checkOutTime)}</td>
                      <td style={{ padding: '6px 8px' }}>{c.method}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {reportData.type === 'roster' && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ borderBottom: '2px solid var(--frost)' }}>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Name</th><th style={{ textAlign: 'left', padding: '8px' }}>Agency</th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Position</th><th style={{ textAlign: 'left', padding: '8px' }}>PIN</th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Status</th>
                </tr></thead>
                <tbody>
                  {reportData.rows.map((p: any, i: number) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--frost)' }}>
                      <td style={{ padding: '6px 8px', fontWeight: 600 }}>{p.firstName} {p.lastName}</td>
                      <td style={{ padding: '6px 8px' }}>{p.agency}</td>
                      <td style={{ padding: '6px 8px' }}>{p.position}</td>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{p.pin}</td>
                      <td style={{ padding: '6px 8px' }}>{p.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {reportData.type === 'meals' && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ borderBottom: '2px solid var(--frost)' }}>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Name</th><th style={{ textAlign: 'left', padding: '8px' }}>Agency</th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Event</th><th style={{ textAlign: 'left', padding: '8px' }}>Meal</th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Date</th><th style={{ textAlign: 'left', padding: '8px' }}>Time</th>
                </tr></thead>
                <tbody>
                  {reportData.checkins.map((c: any, i: number) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--frost)' }}>
                      <td style={{ padding: '6px 8px', fontWeight: 600 }}>{c.personnelName}</td>
                      <td style={{ padding: '6px 8px' }}>{c.agency}</td>
                      <td style={{ padding: '6px 8px' }}>{c.eventName}</td>
                      <td style={{ padding: '6px 8px' }}>{c.mealType}</td>
                      <td style={{ padding: '6px 8px' }}>{c.eventDate}</td>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 12 }}>{fmtTime(c.checkInTime)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {reportData.type === 'hours' && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ borderBottom: '2px solid var(--frost)' }}>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Name</th><th style={{ textAlign: 'left', padding: '8px' }}>Agency</th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Position</th><th style={{ textAlign: 'left', padding: '8px' }}>Shifts</th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Hours</th>
                </tr></thead>
                <tbody>
                  {reportData.rows.map((r: any, i: number) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--frost)' }}>
                      <td style={{ padding: '6px 8px', fontWeight: 600 }}>{r.personnelName}</td>
                      <td style={{ padding: '6px 8px' }}>{r.agency}</td>
                      <td style={{ padding: '6px 8px' }}>{r.position}</td>
                      <td style={{ padding: '6px 8px' }}>{r.shifts}</td>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.totalHours.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {reportData.type === 'staffing' && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ borderBottom: '2px solid var(--frost)' }}>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Date</th><th style={{ textAlign: 'left', padding: '8px' }}>Personnel</th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Check-ins</th><th style={{ textAlign: 'left', padding: '8px' }}>Shifts</th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Meals</th><th style={{ textAlign: 'left', padding: '8px' }}>Trainings</th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Briefings</th>
                </tr></thead>
                <tbody>
                  {reportData.rows.map((r: any, i: number) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--frost)' }}>
                      <td style={{ padding: '6px 8px', fontWeight: 600 }}>{r.date}</td>
                      <td style={{ padding: '6px 8px' }}>{r.uniqueCount}</td>
                      <td style={{ padding: '6px 8px' }}>{r.total}</td>
                      <td style={{ padding: '6px 8px' }}>{r.shifts}</td>
                      <td style={{ padding: '6px 8px' }}>{r.meals}</td>
                      <td style={{ padding: '6px 8px' }}>{r.trainings}</td>
                      <td style={{ padding: '6px 8px' }}>{r.briefings}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {rowCount === 0 && <Text size="sm" c="dimmed" ta="center" py="md">No data found for selected filters.</Text>}
          </div>
        </Paper>
      )}
    </div>
  );
}
